import { type ReactNode } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import Input from "./ui/input";
import Button from "./ui/button";

const signupSchema = z.object({
	first_name: z.string().min(1, "First name is required"),
	last_name: z.string().min(1, "Last name is required"),
	email: z.string().email("Please enter valid email address"),
});

type SignupFormData = z.infer<typeof signupSchema>;

interface SignupFormProps {
	onSubmit: (data: SignupFormData) => void;
	isLoading?: boolean;
	error?: string | null;
	defaultEmail?: string;
	badge?: ReactNode;
}

export type { SignupFormData };

export default function SignupForm({ onSubmit, isLoading = false, error, defaultEmail, badge }: SignupFormProps) {
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<SignupFormData>({
		resolver: zodResolver(signupSchema),
		defaultValues: {
			first_name: "",
			last_name: "",
			email: defaultEmail || "",
		},
	});

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<div className="space-y-4">
				<Input label="First Name" type="text" disabled={isLoading} error={errors.first_name?.message} {...register("first_name")} />
				<Input label="Last Name" type="text" disabled={isLoading} error={errors.last_name?.message} {...register("last_name")} />
				<div className="relative">
					<Input label="Email" type="email" disabled={isLoading} error={errors.email?.message} {...register("email")} />
					{badge}
				</div>
				{error && <p className="text-sm text-red-500">{error}</p>}
				<div className="pt-4">
					<Button type="submit" disabled={isLoading} className="w-full">
						{isLoading ? "Creating..." : "Create account"}
					</Button>
				</div>
			</div>
		</form>
	);
}
